import { useUpdateZone, useZones } from "@/hooks/use-zones";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import type { ZoneConfig, ZoneReservationMode } from "@/store/types";

// Who may put a reservation in the zone. The hint is what staff see when
// choosing: the difference between the last two is whether the system may
// move a booking there.
export const RESERVATION_MODES: { value: ZoneReservationMode; label: string; hint: string }[] = [
  { value: "open", label: "Abierta", hint: "Reservas web los días marcados, staff y walk-ins." },
  {
    value: "staff_only",
    label: "Solo staff",
    hint: "Sin reservas web. El staff reserva aquí y el sistema puede mover reservas a esta zona.",
  },
  {
    value: "walk_in_only",
    label: "Solo walk-ins",
    hint: "Nunca se sugiere para una reserva ni para mover reservas. Solo para quien llega.",
  },
];

// 0=Mon … 6=Sun to match the backend DayOfWeek enum.
const DAYS = [
  { value: 0, short: "Lun" },
  { value: 1, short: "Mar" },
  { value: 2, short: "Mié" },
  { value: 3, short: "Jue" },
  { value: 4, short: "Vie" },
  { value: 5, short: "Sáb" },
  { value: 6, short: "Dom" },
] as const;

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

export function ZonesBookabilityPanel() {
  const { data: zones, isPending } = useZones();
  const updateMut = useUpdateZone();

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="size-6 text-text-secondary" />
      </div>
    );
  }

  const bookableZones = (zones ?? []).filter((z: ZoneConfig) => !z.isBlock);
  // The zone whose PATCH is in flight, so we can show a spinner on its row and
  // lock its controls until the server confirms (prevents double-toggles and
  // gives feedback if the request is slow or fails).
  const savingZoneId = updateMut.isPending ? updateMut.variables?.id : undefined;

  const toggle = (zone: ZoneConfig, day: number) => {
    const current = zone.publicBookableWeekdays ?? ALL_DAYS;
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day].toSorted((a, b) => a - b);
    updateMut.mutate({ id: zone.id, changes: { publicBookableWeekdays: next } });
  };

  const setAll = (zone: ZoneConfig, days: number[]) => {
    updateMut.mutate({ id: zone.id, changes: { publicBookableWeekdays: days } });
  };

  const setMode = (zone: ZoneConfig, mode: ZoneReservationMode) => {
    updateMut.mutate({ id: zone.id, changes: { reservationMode: mode } });
  };

  return (
    <section className="flex flex-col gap-4 max-w-3xl">
      <header>
        <h2 className="text-xl font-semibold tracking-tight">Reservas por zona</h2>
        <p className="text-sm text-text-secondary mt-1">
          Elige quién puede reservar en cada zona. Una zona abierta se reserva desde la web los días
          marcados; los demás días queda solo para staff. Los walk-ins se pueden sentar en cualquier
          zona.
        </p>
      </header>

      <div className="flex flex-col gap-2">
        {bookableZones.map((zone: ZoneConfig) => {
          const weekdays = zone.publicBookableWeekdays ?? ALL_DAYS;
          const mode = zone.reservationMode ?? "open";
          const isSaving = savingZoneId === zone.id;
          return (
            <div
              key={zone.id}
              className="flex flex-col gap-3 rounded-xl border border-border bg-white p-3"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="size-3 shrink-0 rounded-full border"
                    style={{ backgroundColor: zone.color, borderColor: zone.borderColor }}
                  />
                  <span className="font-semibold text-sm truncate">{zone.name}</span>
                  {isSaving && <Spinner className="size-3.5 shrink-0 text-text-secondary" />}
                </div>
                <ToggleGroup
                  aria-label={`Reservas en ${zone.name}`}
                  variant="outline"
                  size="sm"
                  spacing={0}
                  value={[mode]}
                  onValueChange={(next) => {
                    const picked = next[0] as ZoneReservationMode | undefined;
                    if (picked && picked !== mode) setMode(zone, picked);
                  }}
                  disabled={isSaving}
                >
                  {RESERVATION_MODES.map((m) => (
                    <ToggleGroupItem key={m.value} value={m.value} className="text-[12px] px-2.5">
                      {m.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <p className="text-[12px] text-text-muted -mt-1">
                {RESERVATION_MODES.find((m) => m.value === mode)?.hint}
              </p>

              {mode === "open" && (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-1">
                    {DAYS.map((d) => {
                      const on = weekdays.includes(d.value);
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => toggle(zone, d.value)}
                          disabled={isSaving}
                          aria-pressed={on}
                          className={cn(
                            "h-8 w-10 rounded-md text-[12px] font-medium transition-colors",
                            "disabled:opacity-50 disabled:pointer-events-none",
                            on
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-text-muted hover:bg-muted/70",
                          )}
                        >
                          {d.short}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-col gap-0.5 ml-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 px-1.5 text-[10px]"
                      disabled={isSaving}
                      onClick={() => setAll(zone, ALL_DAYS)}
                    >
                      Todos
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 px-1.5 text-[10px]"
                      disabled={isSaving}
                      onClick={() => setAll(zone, [])}
                    >
                      Ninguno
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {bookableZones.length === 0 && (
          <p className="text-sm text-text-muted py-2">No hay zonas configuradas.</p>
        )}
      </div>
    </section>
  );
}

export default ZonesBookabilityPanel;
